import {
  IDENTITY_ERROR_CODES,
  type PaymentMethod,
  paymentMethodListResponseSchema,
  paymentMethodSchema,
  setupPaymentMethodResponseSchema,
} from '@noconflict/contracts';
import { ApiRequestError, apiFetch } from '../../shared/api/http';
import { identityErrorMessage } from '../../shared/api/identity-error';
import type { Supabase } from '../../shared/api/supabase';
import { i18n } from '../../shared/i18n';

/**
 * The organization's stored cards. Reads come from the mirror through RLS (`billing.view`); everything that
 * changes a card goes through the worker, which asks Stripe and refreshes the mirror. The Stripe payment method id
 * is never selected here: charging is the worker's business.
 */

const COLUMNS =
  'id, organization_id, brand, last4, exp_month, exp_year, is_default, added_by, created_at';

export async function listPaymentMethods(
  client: Supabase,
  organizationId: string,
): Promise<PaymentMethod[]> {
  const { data, error } = await client
    .from('org_payment_methods')
    .select(COLUMNS)
    .eq('organization_id', organizationId)
    .order('created_at');
  if (error) throw new Error(error.message);
  return (data ?? []).map((row) => paymentMethodSchema.parse(row));
}

/** The names behind `added_by`, for the cards that have one. A co-member may read a co-member's profile. */
export async function getCardAdders(
  client: Supabase,
  userIds: readonly string[],
): Promise<Record<string, string>> {
  if (userIds.length === 0) return {};
  const { data, error } = await client
    .from('profiles')
    .select('user_id, first_name, last_name')
    .in('user_id', [...userIds]);
  if (error) throw new Error(error.message);
  return Object.fromEntries(
    (data ?? []).map((p) => [p.user_id, `${p.first_name} ${p.last_name}`.trim()]),
  );
}

/**
 * Is there a card a one-step submit could charge? Yes or no, for anyone who may order or pay in the organization:
 * the cards themselves need `billing.view`, which an orderer does not have.
 */
export async function getOrganizationHasDefaultCard(
  client: Supabase,
  organizationId: string,
): Promise<boolean> {
  const { data, error } = await client.rpc('organization_has_default_card', {
    p_organization_id: organizationId,
  });
  if (error) throw new Error(error.message);
  return data === true;
}

const base = (organizationId: string) => `/api/orgs/${organizationId}/payment-methods`;

/** Opens the form that stores a card without taking a payment. */
export const createCardSetup = (token: string, organizationId: string) =>
  apiFetch(`${base(organizationId)}/setup`, setupPaymentMethodResponseSchema, {
    method: 'POST',
    token,
  });

/** Refreshes the mirror from Stripe and answers with it; Stripe is the source of truth. */
export const syncPaymentMethods = (token: string, organizationId: string) =>
  apiFetch(`${base(organizationId)}/sync`, paymentMethodListResponseSchema, {
    method: 'POST',
    token,
  }).then((r) => r.paymentMethods);

export const setDefaultPaymentMethod = (
  token: string,
  organizationId: string,
  paymentMethodId: string,
) =>
  apiFetch(`${base(organizationId)}/${paymentMethodId}/default`, paymentMethodListResponseSchema, {
    method: 'POST',
    token,
  }).then((r) => r.paymentMethods);

export const removePaymentMethod = (
  token: string,
  organizationId: string,
  paymentMethodId: string,
) =>
  apiFetch(`${base(organizationId)}/${paymentMethodId}`, paymentMethodListResponseSchema, {
    method: 'DELETE',
    token,
  }).then((r) => r.paymentMethods);

const identityCodes = new Set<string>(IDENTITY_ERROR_CODES);

/** Catalogue text for a failed card call: the worker passes a database refusal through as its error code. */
export function paymentMethodErrorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) {
    if (identityCodes.has(error.code)) return identityErrorMessage(new Error(error.code));
    return error.message;
  }
  return i18n.t('errors:identity.generic');
}
